import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { ChangeEvent } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/seo/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import LeakCard from "@/components/feature/LeakCard";
import { SITE_ORIGIN } from "@/lib/leaks";
import { categories, leaks } from "@/mocks/leaks";

export default function Feed() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("cat") ?? "all";
  const query = searchParams.get("q") ?? "";
  const [activeTag, setActiveTag] = useState("all");

  const allTags = useMemo(() => {
    const set = new Set<string>();
    leaks.forEach((leak) => leak.tags.forEach((tag) => set.add(tag)));
    return Array.from(set);
  }, []);

  const results = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return leaks.filter((leak) => {
      const matchCategory =
        activeCategory === "all" || leak.category === activeCategory;
      const matchTag = activeTag === "all" || leak.tags.includes(activeTag);
      const matchKeyword =
        !keyword ||
        leak.title.toLowerCase().includes(keyword) ||
        leak.summary.toLowerCase().includes(keyword) ||
        leak.tags.some((tag) => tag.toLowerCase().includes(keyword));
      return matchCategory && matchTag && matchKeyword;
    });
  }, [activeCategory, activeTag, query]);

  const setCategory = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    if (slug === "all") next.delete("cat");
    else next.set("cat", slug);
    setSearchParams(next, { replace: true });
  };

  const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const next = new URLSearchParams(searchParams);
    const value = event.target.value;
    if (value) next.set("q", value);
    else next.delete("q");
    setSearchParams(next, { replace: true });
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "蝌蚪爆料最新爆料列表",
    url: `${SITE_ORIGIN}/feed`,
    description: "蝌蚪爆料快讯与现场线索的完整列表，支持分类、标签与关键词筛选。",
    mainEntity: {
      "@type": "ItemList",
      itemListElement: results.map((leak, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `${SITE_ORIGIN}/leak/${leak.id}`,
        name: leak.title,
      })),
    },
  };

  return (
    <SiteLayout>
      <Seo
        title="蝌蚪爆料最新爆料列表 | 蝌蚪爆料快讯分类筛选"
        description="蝌蚪爆料最新爆料列表，汇总现场线索、首发内幕与网络热议，支持按主题分类、标签与关键词快速筛选，随时掌握蝌蚪爆料快讯。"
        keywords="蝌蚪爆料最新,蝌蚪爆料快讯,蝌蚪爆料列表,蝌蚪爆料现场,蝌快讯"
        path="/feed"
        jsonLd={jsonLd}
      />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-9 md:px-8 md:py-12">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "最新爆料" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            蝌蚪爆料最新爆料列表
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            这里汇总了所有已发布的现场线索、首发内幕与热议话题。你可以切换主题分类、点选标签，或直接输入关键词，
            快速找到想看的那一条爆料。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1200px] px-4 py-10 md:px-8 md:py-14">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="主题分类">
              <button
                type="button"
                role="tab"
                aria-selected={activeCategory === "all"}
                onClick={() => setCategory("all")}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  activeCategory === "all"
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  type="button"
                  role="tab"
                  aria-selected={activeCategory === cat.slug}
                  onClick={() => setCategory(cat.slug)}
                  className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    activeCategory === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                  }`}
                >
                  <i className={`${cat.icon} text-base`} />
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex items-center rounded-full border border-background-300 bg-background-50 pl-3 pr-1 md:w-64">
              <i className="ri-search-line text-sm text-foreground-500" />
              <input
                type="search"
                value={query}
                onChange={onSearch}
                placeholder="搜索标题或标签"
                aria-label="搜索爆料标题或标签"
                className="w-full bg-transparent px-2 py-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-background-200 pt-4">
            <span className="text-xs font-medium text-foreground-500">标签筛选：</span>
            <button
              type="button"
              onClick={() => setActiveTag("all")}
              className={`whitespace-nowrap rounded-md px-2.5 py-1 text-xs transition-colors ${
                activeTag === "all"
                  ? "bg-secondary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              不限
            </button>
            {allTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setActiveTag(tag === activeTag ? "all" : tag)}
                className={`whitespace-nowrap rounded-md px-2.5 py-1 text-xs transition-colors ${
                  activeTag === tag
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        <p className="mt-6 text-sm text-foreground-500">
          共找到 <span className="font-medium text-foreground-900">{results.length}</span> 条爆料
          {activeCategory !== "all"
            ? ` · ${categories.find((cat) => cat.slug === activeCategory)?.name}`
            : ""}
        </p>

        {results.length > 0 ? (
          <div className="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {results.map((leak) => (
              <LeakCard key={leak.id} leak={leak} />
            ))}
          </div>
        ) : (
          <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl" />
            </span>
            <p className="mt-4 font-heading text-lg font-semibold text-foreground-900">
              没有找到匹配的爆料
            </p>
            <p className="mt-2 max-w-sm text-sm text-foreground-600">
              试试换一个关键词，或把分类和标签切回不限，就能看到全部内容。
            </p>
            <button
              type="button"
              onClick={() => {
                setActiveTag("all");
                setSearchParams({}, { replace: true });
              }}
              className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选条件
            </button>
          </div>
        )}
      </section>
    </SiteLayout>
  );
}